/**
 * The plain auth pages (NewsletterSignupPage with NewsletterSignupBlock,
 * ForgotPasswordPage, ResetPasswordPage) as the sleepy-ui 'page' surface
 * takes them (VIDOS src/shadygram-ui/Pages.h's AuthPageSpec): the w-80
 * column's blocks in each page's state, with the texts the page shows. The
 * inputs stay the page's real <form>'s, placed as islands in the drawn
 * frames (each field names its element). Pure: the pages gather their
 * state, this only derives.
 */

export type EmbedPageButtonLook = 'primary' | 'secondary' | 'pill' | 'prominentPill' | 'link';

export interface EmbedPageButton {
  id: string;
  label: string;
  look: EmbedPageButtonLook;
  disabled?: boolean;
  /** An <a> (a router Link), not a <button>: no 44px touch target. */
  link?: boolean;
  /** An icon-only button (an actions row's): the label is its accessible name. */
  icon?: string;
}

export interface EmbedPageRun {
  text: string;
  strong?: boolean;
}

export interface EmbedPageBlock {
  kind: 'header' | 'paragraph' | 'field' | 'actions' | 'button' | 'footer' | 'pill';
  heading?: string;
  small?: boolean;
  text?: EmbedPageRun[];
  /** #rrggbb */
  color?: string;
  label?: string;
  island?: string;
  /** A field whose island is a native <select>: a pixel shorter than an <input> in Chrome. */
  select?: boolean;
  buttons?: EmbedPageButton[];
  pt?: number;
}

export interface EmbedPageModel {
  shown: true;
  name: string;
  /** The column's gap, CSS px. */
  gap: number;
  centered: boolean;
  blocks: EmbedPageBlock[];
}

/** The one email shape every signup / reset / subscribe form checks before it asks the server. */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The pages' buttons, as their events' `key` names them. */
export const PAGE_BUTTON = {
  submit: 'submit',
  cancel: 'cancel',
  back: 'back',
  request: 'request',
  fullAccount: 'fullAccount',
  home: 'home',
} as const;

// Tailwind's spacing and colours the pages use.
const GAP_3 = 12;
const GAP_4 = 16;
const PT_1 = 4;
const PT_2 = 8;
export const ZINC_400 = '#a1a1aa';
export const ZINC_500 = '#71717a';
export const RED_400 = '#f87171';
export const AMBER_400 = '#fbbf24';

export const page = (name: string, blocks: EmbedPageBlock[], centered = false, gap = GAP_4): EmbedPageModel =>
  ({ shown: true, name, gap, centered, blocks });
export const header = (heading: string, subtext?: string, small = false): EmbedPageBlock => ({
  kind: 'header', heading, ...(small ? { small } : {}),
  ...(subtext ? { text: [{ text: subtext }], color: ZINC_500 } : {}),
});
export const paragraph = (text: EmbedPageRun[], color: string): EmbedPageBlock => ({ kind: 'paragraph', text, color });
export const field = (label: string, island: string, select = false): EmbedPageBlock => ({ kind: 'field', label, island, ...(select ? { select } : {}) });
export const errorBlock = (error: string | null): EmbedPageBlock[] => (error ? [paragraph([{ text: error }], RED_400)] : []);
export const button = (id: string, label: string, disabled = false): EmbedPageBlock =>
  ({ kind: 'button', buttons: [{ id, label, look: 'primary', ...(disabled ? { disabled } : {}) }] });
export const linkButton = (id: string, label: string): EmbedPageBlock =>
  ({ kind: 'button', buttons: [{ id, label, look: 'primary', link: true }] });

// ---- ForgotPasswordPage -----------------------------------------------------------------

export interface ForgotPasswordPageInput {
  submitted: boolean;
  email: string;
  error: string | null;
  submitting: boolean;
  emailIsland: string;
}

export function forgotPasswordPageModel(input: ForgotPasswordPageInput): EmbedPageModel {
  if (input.submitted) {
    return page('live_forgot_password_sent', [
      header('Check your email'),
      paragraph([
        { text: 'If ' }, { text: input.email, strong: true },
        { text: ' is on file, we sent you a link to reset your password. It expires in 30 minutes.' },
      ], ZINC_400),
      linkButton(PAGE_BUTTON.back, 'Back to sign-in'),
    ], true);
  }
  return page('live_forgot_password', [
    header('Forgot your password?', "Enter the email on your account and we'll send you a reset link."),
    field('Email', input.emailIsland),
    ...errorBlock(input.error),
    {
      kind: 'actions',
      buttons: [
        { id: PAGE_BUTTON.submit, label: input.submitting ? 'Sending…' : 'Send reset link', look: 'primary',
          ...(input.submitting ? { disabled: true } : {}) },
        { id: PAGE_BUTTON.cancel, label: 'Cancel', look: 'secondary' },
      ],
    },
  ]);
}

// ---- ResetPasswordPage ----------------------------------------------------------------------

export interface ResetPasswordPageInput {
  hasToken: boolean;
  error: string | null;
  submitting: boolean;
  passwordIsland: string;
  confirmIsland: string;
}

export function resetPasswordPageModel(input: ResetPasswordPageInput): EmbedPageModel {
  if (!input.hasToken) {
    return page('live_reset_password_invalid', [
      header('Invalid reset link'),
      paragraph([{ text: 'This link is missing its token. Request a new reset email to try again.' }], ZINC_500),
      linkButton(PAGE_BUTTON.request, 'Request a new link'),
    ], true);
  }
  return page('live_reset_password', [
    header('Set a new password', "Pick something at least 8 characters long. You'll be signed out of all devices."),
    field('New password', input.passwordIsland),
    field('Confirm new password', input.confirmIsland),
    ...errorBlock(input.error),
    {
      kind: 'actions',
      buttons: [{ id: PAGE_BUTTON.submit, label: input.submitting ? 'Updating…' : 'Update password', look: 'primary',
        ...(input.submitting ? { disabled: true } : {}) }],
    },
  ]);
}

// ---- NewsletterSignupPage ---------------------------------------------------------------------

/** The page's footer on-ramp: an org's homepage link, the full-account pill, or none. */
export type NewsletterFooter = { id: string; label: string } | null;

export const FULL_ACCOUNT_LABEL = 'Want the full experience? Create a free account on shadygram →';
export const HOME_LINK_LABEL = 'Visit our homepage →';

export interface NewsletterPageInput {
  /** Arrived from the confirmation email (?confirmed=1). */
  confirmed: boolean;
  confirmedEmail?: string;
  listLabel: string;
  headline: string;
  subtext: string;
  confirmError: boolean;
  submitted: boolean;
  email: string;
  error: string | null;
  submitting: boolean;
  footer: NewsletterFooter;
  emailIsland: string;
}

const pill = (footer: NewsletterFooter, prominent: boolean, pt: number): EmbedPageBlock[] =>
  (footer ? [{ kind: 'pill', pt, buttons: [{ id: footer.id, label: footer.label, look: prominent ? 'prominentPill' : 'pill' }] }]
    : []);

export function newsletterPageModel(input: NewsletterPageInput): EmbedPageModel {
  if (input.confirmed) {
    const subscribed: EmbedPageRun[] = input.confirmedEmail
      ? [{ text: input.confirmedEmail, strong: true }, { text: ' is confirmed.' }]
      : [{ text: 'Your subscription is confirmed.' }];
    return page('live_newsletter_confirmed', [
      header("You're subscribed! 🎉"),
      paragraph([...subscribed, { text: ` You'll start receiving updates from ${input.listLabel}.` }], ZINC_400),
      ...pill(input.footer, true, PT_2),
    ], true);
  }
  if (input.submitted) {
    return page('live_newsletter_sent', [
      header('Check your email', undefined, true),
      paragraph([
        { text: 'We sent a confirmation link to ' }, { text: input.email, strong: true },
        { text: '. Click it to finish subscribing. The link expires in 7 days.' },
      ], ZINC_400),
      ...pill(input.footer, false, PT_1),
    ], true, GAP_3);
  }
  return page('live_newsletter', [
    header(input.headline, input.subtext),
    ...(input.confirmError ? [paragraph([{
      text: 'That confirmation link was invalid or has expired. Enter your email to get a fresh one.',
    }], AMBER_400)] : []),
    field('Email', input.emailIsland),
    ...errorBlock(input.error),
    button(PAGE_BUTTON.submit, input.submitting ? 'Subscribing…' : 'Subscribe', input.submitting),
    ...(input.footer ? [{
      kind: 'footer' as const, label: 'or', buttons: [{ id: input.footer.id, label: input.footer.label, look: 'pill' as const }],
    }] : []),
  ]);
}

// ---- VerifyEmailPage ----------------------------------------------------------------------------

export interface VerifyEmailPageInput {
  /** The verification outcome: 'verifying' is the page's loading state (the host shows the branded spinner). */
  status: 'verifying' | 'success' | 'error';
  /** The real failure cause (the server's, or the missing token). */
  error: string | null;
}

/** The finished states of the signup verification link; null while it is still being checked. */
export function verifyEmailPageModel(input: VerifyEmailPageInput): EmbedPageModel | null {
  if (input.status === 'success') {
    return page('live_verify_email_success', [
      header("You're in!"),
      paragraph([{ text: 'Redirecting you to the app…' }], ZINC_400),
    ], true);
  }
  if (input.status === 'error') {
    return page('live_verify_email_failed', [
      header('Verification failed'),
      paragraph([{ text: input.error || 'Verification failed.' }], RED_400),
      linkButton(PAGE_BUTTON.back, 'Back to sign-in'),
    ], true);
  }
  return null;
}
